import { Formik } from "formik";
import * as _ from "lodash";
import * as React from "react";
import { toast } from "react-toastify";
import { useMappedState } from "redux-react-hook";
import styled, { css } from "styled-components";

import { IApiError } from "src/library/utils/errors/apiError";
import { Button } from "src/library/components/Button";
import { CascadeSetting } from "src/library/components/Forms";
import { ButtonLink } from "src/library/components/Link";
import Grid from "src/library/components/Hedron";
import DropInfoSidebar from "src/core/drops/components/DropInfoSidebar";
import {
  InfoBarWrapper,
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import useCascadeSettings, {
  cascadeSettingsSchema,
  IFormFieldValues
} from "src/core/shared/components/useCascadeSettings";
import { useInheritedSettingsApi } from "src/core/shared/api/inheritedSettings.api";
import type { IStore } from "src/core/shared/store";
import { setGlobalError } from "src/core/shared/store/global/global.actions";
import { useDrop } from "src/core/drops/hooks";
import { PageProps } from "src/pages/Router/routes";
import {
  InheritedSettingNameType,
  OrderedInheritedSettings,
  IInheritedSetting,
  InheritedSettingValue
} from "src/core/drops/api/drops.api";
import { ERPType } from "src/core/shared/api/globalSettings.api";
import useCheckUserPermission from "src/core/users/hooks/useCheckUserPermission";
import { UpdatedSettingsFields } from "src/library/components/Forms/CascadeSettingField";

interface UsersPageProps {
  customerId: string;
  dropId: string;
}

/**
 * Drop settings page
 *
 */
const CustomerOrderSettingsPage: React.ReactFCWithChildren<PageProps<unknown, UsersPageProps>> = props => {
  const { match, terminology } = props;
  const customerId = match ? parseInt(match.params.customerId, 0) : 0;
  const dropId = match ? parseInt(match.params.dropId, 0) : 0;
  const [ { isLoading: isLoadingDrop, drop } ] = useDrop(dropId);
  const { erp } = useMappedState(({ global }: IStore) => ({ erp: global.settings && global.settings.erp.name }));
  const [ getSettingsResponse, getSettings ] = useInheritedSettingsApi("GET_BRANCH_SETTINGS");
  const [ inheritedValues, setInheritedValues ] = React.useState<OrderedInheritedSettings<IInheritedSetting>>();
  const [ overrideValues, setOverrideValues ] = React.useState<OrderedInheritedSettings<IInheritedSetting>>();
  const formRef = React.useRef<Formik<IFormFieldValues>>(null);
  const [ requestsCount, setRequestsCount ] = React.useState<number | null>(null);
  const [ errors, setErrors ] = React.useState<IApiError[]>([]);
  const checkPermission = useCheckUserPermission();
  const [ initialValues, setSettings ] = useCascadeSettings(terminology, terminology.drop, drop, inheritedValues, overrideValues);
  const [ settingsToUpdate, setSettingsToUpdate ] = React.useState<UpdatedSettingsFields>([]);
  const [ isSubmitting, setIsSubmitting ] = React.useState(false);

  const canAmend = React.useMemo(() => {
    return checkPermission("amend-global-settings");
  }, [ checkPermission ]);

  React.useEffect(() => {
    getSettings({ dropId });
  }, [ dropId, getSettings ]);

  React.useEffect(() => {
    if (getSettingsResponse.data) {
      const inheritedToSet: OrderedInheritedSettings<IInheritedSetting> = {};
      const overridesToSet: OrderedInheritedSettings<IInheritedSetting> = {};

      _.each(getSettingsResponse.data, useCascadeSettings => {
        const customerSetting = _.find(useCascadeSettings, setting => setting.scope === "customer");
        const globalSetting = _.find(useCascadeSettings, setting => setting.scope === "global");
        const branchSetting = _.find(useCascadeSettings, setting => setting.scope === "branch");

        if (branchSetting) {
          overridesToSet[ branchSetting.name ] = branchSetting;
        }

        if (customerSetting) {
          inheritedToSet[ customerSetting.name ] = customerSetting;
        } else if (globalSetting) {
          inheritedToSet[ globalSetting.name ] = globalSetting;
        }
      });
      setOverrideValues(overridesToSet);
      setInheritedValues(inheritedToSet);
    }

    if (getSettingsResponse.error) {
      setGlobalError(getSettingsResponse.error);
    }
  }, [ getSettingsResponse ]);

  React.useEffect(() => {
    if (requestsCount === 0) {
      setSettings(null);

      if (errors.length === 0) {
        toast.success("Settings saved!");
      } else {
        if (errors.length === 1) {
          toast.error(`Could not save settings: ${errors[ 0 ].message}. Please try again.`);
        } else {
          toast.error(errors[ 0 ].message);
        }

        setErrors([]);
      }

      getSettings({ dropId });
    }
  }, [
    requestsCount,
    errors,
    setSettings,
    getSettings,
    dropId
  ]);

  // This preps what settings need to be updated and saves them in settingsToUpdate list
  // The list is then handled by each CascadeSetting component which actions the update
  const handleSubmitSettings = (form: IFormFieldValues) => {
    const requests: UpdatedSettingsFields = [];

    _.each(form, setting => {
      if (setting) {
        const initialValue: IInheritedSetting<keyof InheritedSettingValue> | null = (initialValues && initialValues[ setting.name ]) || null;
        const originalOverride = (overrideValues && overrideValues[ setting.name ]) || null;

        if (originalOverride) {
          if (setting.inherited) {
            // original override found
            requests.push({
              params: {
                name: setting.name,
                id: originalOverride.id
              },
              action: "delete"
            });
          } else {
            const params = {
              ...originalOverride,
              value: setting.value
            };

            if (setting.name === "delivery_route") {
              params.deliveryRoute = setting.value;
            }

            // Only push update if value has changed
            if (setting.value !== initialValue?.value) {
              requests.push({
                params,
                action: "update"
              });
            }
          }
        } else {
          // no original override
          if (!setting.inherited) {
            const params: Omit<IInheritedSetting, "id"> = {
              scope: "branch",
              value: setting.value,
              entityId: dropId,
              name: setting.name
            };

            if (setting.name === "delivery_route") {
              params.deliveryRoute = setting.value;
            }

            requests.push({
              params,
              action: "create"
            });
          }
        }
      }
    });
    setRequestsCount(requests.length);
    setSettingsToUpdate(requests);
  };

  const submitForm = () => {
    if (formRef.current) {
      formRef.current.submitForm();
    }
  };

  React.useEffect(() => {
    if (requestsCount === 0 && !getSettingsResponse.pending) {
      if (formRef.current && initialValues) {
        formRef.current.setValues(initialValues);
      }
    }
  }, [ initialValues ]);

  const formatErrors = (errors: any, settingName: InheritedSettingNameType) => {
    if (errors) {
      if (errors[ settingName ]) {
        if (typeof errors[ settingName ].value === "object") {
          // TODO: neaten with recursive function
          const result = Object.keys(errors[ settingName ].value).reduce((r, k) => {
            if (typeof errors[ settingName ].value[ k ] === "object") {
              return Object.keys(errors[ settingName ].value[ k ]).reduce((_n, p) => {
                return r.concat(errors[ settingName ].value[ k ][ p ]);
              }, []);
            }

            return r.concat(errors[ settingName ].value[ k ]);
          }, []);

          return `${result}`;
        }

        return `${errors[ settingName ].value}`;
      }
    }

    return null;
  };

  return (
    <Layout
      {...props}
      title={drop ? `${drop.name} - Defaults & Limits` : ""}
    >
      <MainContentWrapper fill>
        <Grid.Bounds direction="vertical">
          {initialValues && (
            <Formik
              ref={formRef}
              initialValues={initialValues}
              validationSchema={cascadeSettingsSchema(terminology)}
              validateOnChange
              validateOnBlur
              onSubmit={handleSubmitSettings}
              render={({
                handleSubmit, handleBlur, values, setFieldValue, errors
              }) => {
                return (
                  <form onSubmit={handleSubmit}>
                    {_.map(values, setting => {
                      if (setting) {
                        if (setting.hidden) {
                          return null;
                        }

                        return (
                          <CascadeSettingWrapper
                            id={`${setting.name}`}
                            key={setting.name}
                          >
                            {setting.Description}

                            {errors[ setting.name ] && (
                              <InlineError>
                                {formatErrors(errors, setting.name)}
                              </InlineError>
                            )}

                            <CascadeSetting
                              erp={erp as ERPType}
                              name={setting.name}
                              label={setting.label}
                              defaultChecked={setting.inherited}
                              disabled={!canAmend}
                              setDefaultChecked={defaultChecked => {
                                if (defaultChecked) {
                                  setFieldValue(setting.name, {
                                    ...setting,
                                    inherited: true,
                                    value: setting.inheritedValue
                                  });
                                } else {
                                  setFieldValue(setting.name, {
                                    ...setting,
                                    inherited: false
                                  });
                                }
                              }}
                              SettingElement={setting.SettingElement(setFieldValue, setting, handleBlur, !canAmend)}
                              // Props to manage saving updates to settings
                              settingsToUpdate={settingsToUpdate}
                              updateSettingsToUpdate={setSettingsToUpdate}
                              updateIsSubmiting={setIsSubmitting}
                              updateRequestsCount={setRequestsCount}
                              updateErrors={setErrors}
                            />
                          </CascadeSettingWrapper>
                        );
                      }

                      return null;
                    })}
                  </form>
                );
              }}
            />
          )}
        </Grid.Bounds>
      </MainContentWrapper>

      <InfoBarWrapper>
        <DropInfoSidebar
          terminology={terminology}
          isLoading={isLoadingDrop}
          data={drop}
          primaryAction={(
            <ButtonLink
              $buttonType="secondary"
              to={`/admin/customers/${customerId}/drops`}
            >
              Cancel
            </ButtonLink>
          )}
          secondaryAction={(
            <Button
              loading={isSubmitting}
              onClick={submitForm}
              disabled={!canAmend}
            >
              Save
            </Button>
          )}
        />
      </InfoBarWrapper>
    </Layout>
  );
};

const InlineError = styled.div`
  ${({ theme }) => css`
    background-color: ${theme.colors.error};
    padding: ${theme.spacing.padding.sm};
    border-radius: ${theme.borderRadius};
  `}
  color: white;
`;

const CascadeSettingWrapper = styled.div`
  padding-bottom: ${({ theme }) => theme.spacing.padding.lg};
`;

export default CustomerOrderSettingsPage;
